import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import RelatorioHtmlFrame, { ContextoRelatorio } from '../../components/RelatorioHtmlFrame'
import Janela from '../componentes/Janela'
import AvisoGuiaNovo, { avisarGuiaBaixado } from '../../views/RelatorioHtml/AvisoGuiaNovo'
import { plural } from './comum'
import { baixarTexto, lerTemplate, MAX_BYTES_DO_ARQUIVO, Template, templateParaArquivo, Tokens } from './importarTemplate'
import './gestao.scss'

interface Resumo { id?: number | null; nome: string; descricao?: string | null; padrao: boolean; relatorios: number; atualizadoEm?: string | null }

interface RelatorioParaPrevia { dashboardId: number; nome: string; publicado: boolean; templateId?: number | null; ambientes: { id: number; nome: string }[] }
interface EscolhaDaPrevia { dashboardId: number; ambienteId: number }

// A última escolha de relatório para a prévia fica neste navegador, para não escolher de novo a cada visita.
const CHAVE_DA_PREVIA = 'np-templates-previa-relatorio'
const lerEscolhaGuardada = (): EscolhaDaPrevia | null => {
  try {
    const v = JSON.parse(window.localStorage.getItem(CHAVE_DA_PREVIA) || 'null')
    return v && Number.isInteger(v.dashboardId) && Number.isInteger(v.ambienteId) ? v : null
  } catch { return null }
}
const guardarEscolha = (e: EscolhaDaPrevia | null) => {
  try { if (e) window.localStorage.setItem(CHAVE_DA_PREVIA, JSON.stringify(e)); else window.localStorage.removeItem(CHAVE_DA_PREVIA) } catch { /* sem armazenamento: só não lembra */ }
}

// Larguras da prévia ampliada. "Tela toda" usa a janela inteira; as outras mostram o relatório como ele fica em cada aparelho.
const LARGURAS_DA_PREVIA: { id: string; rotulo: string; px: number | null }[] = [
  { id: 'cheia', rotulo: 'Tela toda', px: null },
  { id: '1280', rotulo: 'Desktop 1280', px: 1280 },
  { id: '768', rotulo: 'Tablet 768', px: 768 },
  { id: '375', rotulo: 'Celular 375', px: 375 }
]

const CORES: { chave: keyof Tokens; rotulo: string; dica: string }[] = [
  { chave: 'corPrimaria', rotulo: 'Primária', dica: 'Destaques, série principal, borda dos cartões' },
  { chave: 'corDestaque', rotulo: 'Destaque', dica: 'Segunda cor da marca, com moderação' },
  { chave: 'corSecundaria', rotulo: 'Secundária', dica: 'Comparação, previsto, segunda série' },
  { chave: 'fundo', rotulo: 'Fundo', dica: 'Fundo da página' },
  { chave: 'superficie', rotulo: 'Superfície', dica: 'Fundo de cartões e painéis' },
  { chave: 'texto', rotulo: 'Texto', dica: 'Texto principal' },
  { chave: 'textoSuave', rotulo: 'Texto suave', dica: 'Rótulos, legendas, notas' },
  { chave: 'borda', rotulo: 'Borda', dica: 'Bordas e linhas de tabela' },
  { chave: 'positivo', rotulo: 'Positivo', dica: 'Resultado bom' },
  { chave: 'alerta', rotulo: 'Alerta', dica: 'Atenção' },
  { chave: 'negativo', rotulo: 'Negativo', dica: 'Resultado ruim' }
]
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
const seisDigitos = (cor: string) =>
  /^#[0-9a-fA-F]{3}$/.test(cor) ? '#' + cor.slice(1).split('').map(c => c + c).join('') : HEX.test(cor) ? cor : '#000000'

function CampoCor({ rotulo, dica, valor, aoMudar, desabilitado }: { rotulo: string; dica?: string; valor: string; aoMudar: (v: string) => void; desabilitado?: boolean }) {
  return (
    <label className="ng-cor" title={dica}>
      <input type="color" value={seisDigitos(valor)} disabled={desabilitado} onChange={e => aoMudar(e.target.value)} aria-label={`${rotulo} (seletor)`} />
      <span><small>{rotulo}</small>
        <input value={valor} maxLength={7} disabled={desabilitado} className={HEX.test(valor) ? '' : 'invalido'} onChange={e => aoMudar(e.target.value.trim())} aria-label={rotulo} />
      </span>
    </label>
  )
}

/**
 * Gestão › Templates HTML: a base visual dos relatórios HTML. O portal injeta o template em todo
 * relatório ligado a ele, inclusive nos publicados; por isso a prévia ao lado, e só o administrador salva.
 */
export default function Templates() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify } = useNotification()
  const [lista, setLista] = useState<Resumo[] | null>(null)
  const [template, setTemplate] = useState<Template | null>(null)
  const [original, setOriginal] = useState('')
  const [cssDoPortal, setCssDoPortal] = useState('')
  const [previa, setPrevia] = useState<{ html: string; contexto: ContextoRelatorio } | null>(null)
  const [erroPrevia, setErroPrevia] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [excluir, setExcluir] = useState(false)
  const arquivoLogo = useRef<HTMLInputElement>(null)
  const arquivoImportar = useRef<HTMLInputElement>(null)
  const arquivoSobreEste = useRef<HTMLInputElement>(null)
  const [relatorios, setRelatorios] = useState<RelatorioParaPrevia[]>([])
  // Nulo: a página de demonstração. Com valor: um relatório de verdade, com dados do modelo.
  const [escolha, setEscolha] = useState<EscolhaDaPrevia | null>(null)
  // Prévia ampliada: o mesmo quadro (sem recarregar o relatório) ocupa a janela inteira, na largura escolhida.
  const [larga, setLarga] = useState(false)
  const [larguraDaPrevia, setLarguraDaPrevia] = useState('cheia')
  // Template lido de um .json, à espera de a tela de "novo" abrir (a rota recarrega o formulário).
  const importado = useRef<Template | null>(null)
  const [problemasDaImportacao, setProblemasDaImportacao] = useState<string[] | null>(null)

  // Rota: /templates/portal (embutido), /templates/novo, /templates/{id}.
  const atual = id === 'portal' ? null : id === 'novo' ? 'novo' : Number(id) || undefined

  const carregarLista = useCallback(async () => {
    const r: any = await services.api.templatesHtml()
    setLista(r.fail ? [] : r.data)
  }, [])

  useEffect(() => {
    services.api.templatesHtmlRelatoriosParaPrevia().then((r: any) => {
      const lista: RelatorioParaPrevia[] = r.fail ? [] : r.data
      setRelatorios(lista)
      // Só lembra a escolha se o relatório e o ambiente ainda existem.
      const guardada = lerEscolhaGuardada()
      if (guardada && lista.some(x => x.dashboardId === guardada.dashboardId && x.ambientes.some(a => a.id === guardada.ambienteId))) setEscolha(guardada)
    })
  }, [])

  useEffect(() => {
    carregarLista()
    services.api.templateHtml(null).then((r: any) => { if (!r.fail) setCssDoPortal(r.data.css) })
  }, [carregarLista])

  useEffect(() => {
    setPrevia(null)
    if (atual === undefined) { setTemplate(null); return }
    if (atual === 'novo' && importado.current) {
      const lido = importado.current
      importado.current = null
      setTemplate(lido)
      setOriginal('')
      return
    }
    services.api.templateHtml(atual === 'novo' ? null : atual).then((r: any) => {
      if (r.fail) { notify(r.error || 'Template não encontrado.', { variant: 'error' }); return }
      const t: Template = atual === 'novo' ? { ...r.data, id: null, nome: 'Novo template', descricao: '', padrao: false } : r.data
      setTemplate(t)
      setOriginal(atual === 'novo' ? '' : JSON.stringify(t))
    })
  }, [atual, notify])

  useEffect(() => {
    if (id || !lista?.length || window.innerWidth <= 768) return
    const padrao = lista.find(l => l.padrao) || lista[0]
    navigate(`/app/gestao/templates/${padrao.id ?? 'portal'}`, { replace: true })
  }, [id, lista, navigate])

  // A prévia acompanha a edição, com uma pausa para não chamar o servidor a cada tecla.
  useEffect(() => {
    if (!template) return
    const espera = setTimeout(async () => {
      const r: any = escolha
        ? await services.api.templateHtmlPreviaRelatorio(escolha.dashboardId, escolha.ambienteId, template)
        : await services.api.templateHtmlPrevia(template)
      if (r.fail) setErroPrevia(r.error || 'Não foi possível montar a prévia.')
      else { setErroPrevia(''); setPrevia(r.data) }
    }, 600)
    return () => clearTimeout(espera)
  }, [template, escolha])

  const mudar = (parte: Partial<Template>) => setTemplate(t => (t ? { ...t, ...parte } : t))
  const mudarToken = (parte: Partial<Tokens>) => setTemplate(t => (t ? { ...t, tokens: { ...t.tokens, ...parte } } : t))
  const doPortal = atual === null
  const mudou = !!template && JSON.stringify(template) !== original
  const t = template?.tokens

  const salvar = async () => {
    if (!template) return
    setSalvando(true)
    const r: any = await services.api.templateHtmlSalvar(template.id ?? null, template)
    setSalvando(false)
    if (r.fail) { notify(r.error || 'Não foi possível salvar.', { variant: 'error' }); return }
    notify('Template salvo. Os relatórios ligados a ele já abrem com o visual novo.', { variant: 'success' })
    setTemplate(r.data); setOriginal(JSON.stringify(r.data))
    await carregarLista()
    if (atual === 'novo') navigate(`/app/gestao/templates/${r.data.id}`, { replace: true })
  }

  const definirPadrao = async (alvo: number | null) => {
    const r: any = await services.api.templateHtmlDefinirPadrao(alvo)
    if (r.fail) { notify(r.error || 'Não foi possível definir o padrão.', { variant: 'error' }); return }
    notify('Template padrão atualizado.', { variant: 'success' })
    carregarLista()
    if (template) { const novo = { ...template, padrao: template.id === alvo }; setTemplate(novo); setOriginal(JSON.stringify(novo)) }
  }

  const confirmarExcluir = async () => {
    if (!template?.id) return
    const r: any = await services.api.templateHtmlExcluir(template.id)
    if (r.fail) { notify(r.error || 'Não foi possível excluir.', { variant: 'error' }); return }
    setExcluir(false)
    notify('Template excluído.', { variant: 'success' })
    await carregarLista()
    navigate('/app/gestao/templates')
  }

  const lerLogo = (arquivo?: File) => {
    if (!arquivo) return
    if (arquivo.size > 220_000) { notify('O logo precisa ter até 220 KB. Prefira SVG ou PNG pequeno.', { variant: 'error' }); return }
    const leitor = new FileReader()
    leitor.onload = () => mudar({ logo: String(leitor.result) })
    leitor.readAsDataURL(arquivo)
  }

  useEffect(() => {
    if (!larga) return undefined
    const sair = (e: KeyboardEvent) => { if (e.key === 'Escape') setLarga(false) }
    window.addEventListener('keydown', sair)
    const antes = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', sair); document.body.style.overflow = antes }
  }, [larga])

  const escolherRelatorio = (dashboardId: number | null, ambienteId?: number) => {
    const rel = relatorios.find(r => r.dashboardId === dashboardId)
    const nova = rel ? { dashboardId: rel.dashboardId, ambienteId: ambienteId ?? rel.ambientes[0].id } : null
    setPrevia(null)
    setErroPrevia('')
    setEscolha(nova)
    guardarEscolha(nova)
  }

  // Os dados do relatório aberto na prévia: o mesmo caminho da prévia de quem escreve o relatório, sem a regra por usuário.
  const consultarPrevia = useCallback(async (consulta: unknown) => {
    if (!escolha) throw new Error('A prévia do template não consulta dados.')
    const r: any = await services.api.relatorioHtmlPreviaDados(escolha.dashboardId, escolha.ambienteId, consulta)
    if (r.fail) throw new Error(r.error || 'Falha ao consultar os dados')
    return r.data
  }, [escolha])

  const exportar = () => {
    if (!template) return
    const { nomeArquivo, conteudo } = templateParaArquivo(template)
    baixarTexto(nomeArquivo, conteudo, 'application/json;charset=utf-8')
  }

  const guiaDoClaude = async () => {
    const r: any = await services.api.templatesHtmlGuiaClaude()
    if (r.fail) notify(r.error || 'Não foi possível baixar o guia.', { variant: 'error' })
    else {
      baixarTexto(r.data.nomeArquivo, r.data.conteudo)
      avisarGuiaBaixado()
    }
  }

  // Lê o .json, confere (com a lista completa do que estiver errado) e preenche o formulário de novo template.
  // Nada é gravado: quem importa revisa a prévia e clica em Salvar.
  // Com "sobreEste", os valores do arquivo entram no template aberto (mesmo id), para ajustar sem criar cópia.
  const importar = (arquivo?: File, sobreEste = false) => {
    if (!arquivo) return
    if (arquivo.size > MAX_BYTES_DO_ARQUIVO) {
      setProblemasDaImportacao([`O arquivo tem ${Math.round(arquivo.size / 1000)} KB; um template tem no máximo ${MAX_BYTES_DO_ARQUIVO / 1000} KB (CSS de até 100 KB e logo de até 300 KB).`])
      return
    }
    const leitor = new FileReader()
    leitor.onerror = () => setProblemasDaImportacao(['Não foi possível ler o arquivo.'])
    leitor.onload = () => {
      const r = lerTemplate(String(leitor.result ?? ''))
      if (!r.ok) { setProblemasDaImportacao(r.problemas); return }
      r.avisos.forEach(a => notify(a, { variant: 'info' }))
      if (sobreEste && template?.id != null) {
        setTemplate({ ...r.template, id: template.id, padrao: template.padrao })
        notify(`Valores do arquivo carregados em “${template.nome}”. Confira a prévia e clique em Salvar template para aplicar nos relatórios.`, { variant: 'success' })
        return
      }
      notify(`Template “${r.template.nome}” importado. Confira a prévia e clique em Salvar template.`, { variant: 'success' })
      if (atual === 'novo') { setTemplate(r.template); setOriginal('') }
      else { importado.current = r.template; navigate('/app/gestao/templates/novo') }
    }
    leitor.readAsText(arquivo, 'utf-8')
  }

  const larguraPx = LARGURAS_DA_PREVIA.find(l => l.id === larguraDaPrevia)?.px ?? null
  const escolhido = escolha ? relatorios.find(r => r.dashboardId === escolha.dashboardId) : undefined
  const nomeDoTemplateDoRelatorio = escolhido ? lista?.find(l => (l.id ?? null) === (escolhido.templateId ?? null))?.nome : undefined
  const usos = lista?.find(l => (l.id ?? null) === (template?.id ?? null))?.relatorios || 0

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Templates HTML</h1>
        <div className="ng-acoes">
          <button type="button" className="ng-btn" onClick={guiaDoClaude} title="Baixa o guia que ensina o Claude a criar o arquivo .json de um template"><DownloadIcon />Guia para o Claude</button>
          <input ref={arquivoImportar} type="file" accept=".json,application/json" hidden aria-label="Arquivo do template (.json)" onChange={e => { importar(e.target.files?.[0]); e.target.value = '' }} />
          <button type="button" className="ng-btn" onClick={() => arquivoImportar.current?.click()} title="Preenche um novo template com um arquivo .json, para você revisar e salvar"><UploadIcon />Importar template</button>
          <button type="button" className="ng-btn prim" onClick={() => navigate('/app/gestao/templates/novo')}><AddIcon />Novo template</button>
        </div>
      </div>
      <AvisoGuiaNovo guia="templates" />
      <p className="ng-explic">A base visual dos relatórios HTML: cores, fonte, logo e componentes. O <b>padrão</b> vale para todo relatório que não escolheu um template. Mudar um template muda na hora os relatórios ligados a ele, inclusive os publicados.</p>

      <div className={`ng-ambientes${id ? ' com-detalhe' : ''}`}>
        <aside className="ng-cartao ng-amb-lista" aria-label="Templates">
          <div className="ng-amb-itens">
            {!lista && <p className="ng-explic">Carregando…</p>}
            {lista?.map(l => {
              const chave = l.id ?? 'portal'
              return (
                <button key={chave} type="button" className="ng-amb-item" aria-current={String(chave) === id ? 'page' : undefined} onClick={() => navigate(`/app/gestao/templates/${chave}`)}>
                  <span className="ng-amb-ini">{l.nome.charAt(0)}</span>
                  <span className="ng-amb-txt"><b>{l.nome} {l.padrao && <span className="ng-pill ok">Padrão</span>}</b>
                    <small>{l.id == null ? 'Embutido no portal' : `${plural(l.relatorios, 'relatório escolheu', 'relatórios escolheram')} este`}</small></span>
                </button>
              )
            })}
          </div>
        </aside>

        <section className="ng-amb-detalhe">
          {id && !template && <div className="ng-cartao"><p className="ng-explic">Carregando…</p></div>}
          {template && t && (
            <div className="ng-tpl">
              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <button type="button" className="ng-btn ng-voltar" onClick={() => navigate('/app/gestao/templates')}>‹ Templates</button>
                  <h2>{template.nome} {template.padrao && <span className="ng-pill ok">Padrão</span>}</h2>
                </div>
                {doPortal && <p className="ng-frase">Este é o template embutido no portal e não pode ser alterado. Para mudar, use “Novo template”, que começa com estes valores, e marque o novo como padrão.</p>}
                <div className="ng-campos">
                  <label className="ng-campo largo"><span>Nome</span><input value={template.nome} maxLength={80} disabled={doPortal} onChange={e => mudar({ nome: e.target.value })} /></label>
                  <label className="ng-campo largo"><span>Descrição</span>
                    <textarea rows={2} maxLength={1000} value={template.descricao ?? ''} disabled={doPortal} onChange={e => mudar({ descricao: e.target.value })} />
                    <small>Vai no dicionário: diga ao Claude quando usar este template.</small>
                  </label>
                </div>

                <h3 className="ng-sub" style={{ marginTop: 18 }}>Cores</h3>
                <div className="ng-cores">
                  {CORES.map(c => <CampoCor key={c.chave} rotulo={c.rotulo} dica={c.dica} valor={String(t[c.chave])} desabilitado={doPortal} aoMudar={v => mudarToken({ [c.chave]: v } as Partial<Tokens>)} />)}
                </div>

                <h3 className="ng-sub" style={{ marginTop: 18 }}>Paleta dos gráficos</h3>
                <div className="ng-cores">
                  {t.paleta.map((cor, i) => (
                    <span key={i} style={{ display: 'flex', alignItems: 'center' }}>
                      <CampoCor rotulo={`Série ${i + 1}`} valor={cor} desabilitado={doPortal} aoMudar={v => mudarToken({ paleta: t.paleta.map((c, j) => (j === i ? v : c)) })} />
                      {!doPortal && <button type="button" className="ng-ico perigo" disabled={t.paleta.length <= 1} onClick={() => mudarToken({ paleta: t.paleta.filter((_, j) => j !== i) })} aria-label={`Tirar a série ${i + 1}`}><CloseIcon fontSize="small" /></button>}
                    </span>
                  ))}
                  {!doPortal && t.paleta.length < 12 && <button type="button" className="ng-btn" onClick={() => mudarToken({ paleta: [...t.paleta, '#64748b'] })}><AddIcon />Cor</button>}
                </div>
                <small style={{ color: '#8b92a1' }}>Cada série recebe a cor na ordem. Evite duas cores vizinhas parecidas.</small>

                <h3 className="ng-sub" style={{ marginTop: 18 }}>Fonte e medidas</h3>
                <div className="ng-campos">
                  <label className="ng-campo largo"><span>Fonte (pilha CSS)</span><input value={t.fonte} disabled={doPortal} onChange={e => mudarToken({ fonte: e.target.value })} /><small>Ex.: 'Nunito Sans', 'Segoe UI', sans-serif</small></label>
                  <label className="ng-campo largo"><span>Endereço da fonte (Google Fonts)</span><input value={t.fonteUrl ?? ''} disabled={doPortal} onChange={e => mudarToken({ fonteUrl: e.target.value })} /><small>https://fonts.googleapis.com/css2?family=… · vazio usa a fonte do sistema</small></label>
                  <label className="ng-campo"><span>Arredondamento (px)</span><input type="number" min={0} max={40} value={t.raio} disabled={doPortal} onChange={e => mudarToken({ raio: Number(e.target.value) })} /></label>
                  <label className="ng-campo"><span>Espaçamento (px)</span><input type="number" min={4} max={40} value={t.espaco} disabled={doPortal} onChange={e => mudarToken({ espaco: Number(e.target.value) })} /></label>
                </div>

                <h3 className="ng-sub" style={{ marginTop: 18 }}>Logo</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {template.logo ? <img src={template.logo} alt="Logo" style={{ maxHeight: 40, maxWidth: 160, objectFit: 'contain' }} /> : <span className="ng-explic">Sem logo</span>}
                  <input ref={arquivoLogo} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={e => { lerLogo(e.target.files?.[0]); e.target.value = '' }} />
                  {!doPortal && <button type="button" className="ng-btn" onClick={() => arquivoLogo.current?.click()}>Escolher imagem</button>}
                  {!doPortal && template.logo && <button type="button" className="ng-btn perigo" onClick={() => mudar({ logo: null })}>Remover logo</button>}
                </div>

                <h3 className="ng-sub" style={{ marginTop: 18 }}>CSS dos componentes</h3>
                <label className="ng-campo">
                  <textarea className="ng-codigo" rows={14} value={template.css} disabled={doPortal} onChange={e => mudar({ css: e.target.value })} aria-label="CSS dos componentes" spellCheck={false} />
                  <small>Escreva sobre as variáveis (var(--pt-primaria)) e mantenha o prefixo pt- nas classes. Sem &lt;style&gt; nem &lt;script&gt;.</small>
                </label>
                {!doPortal && cssDoPortal && template.css !== cssDoPortal && <button type="button" className="ng-btn" style={{ marginTop: 8 }} onClick={() => mudar({ css: cssDoPortal })}>Restaurar o CSS do portal</button>}

                <div className="ng-rodape-cartao">
                  <button type="button" className="ng-btn" onClick={exportar} title="Baixa este template como .json (o mesmo formato da importação): para guardar uma cópia ou pedir ajustes ao Claude"><DownloadIcon />Exportar .json</button>
                  {!doPortal && <button type="button" className="ng-btn prim" disabled={salvando || !mudou} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar template'}</button>}
                  {!doPortal && mudou && template.id != null && <button type="button" className="ng-btn" onClick={() => setTemplate(JSON.parse(original))}>Desfazer</button>}
                  {!doPortal && template.id != null && (
                    <>
                      <input ref={arquivoSobreEste} type="file" accept=".json,application/json" hidden aria-label="Arquivo do template para substituir este (.json)" onChange={e => { importar(e.target.files?.[0], true); e.target.value = '' }} />
                      <button type="button" className="ng-btn" onClick={() => arquivoSobreEste.current?.click()} title="Troca os valores deste template pelos de um arquivo .json, sem criar uma cópia. Só vale depois de Salvar.">Importar sobre este</button>
                    </>
                  )}
                  {template.id != null && !template.padrao && <button type="button" className="ng-btn" onClick={() => definirPadrao(template.id ?? null)}>Usar como padrão</button>}
                  {doPortal && !template.padrao && <button type="button" className="ng-btn" onClick={() => definirPadrao(null)}>Voltar a usar este como padrão</button>}
                  {template.id != null && <button type="button" className="ng-btn perigo" style={{ marginLeft: 'auto' }} onClick={() => setExcluir(true)}>Excluir</button>}
                </div>
              </div>

              <div className={`ng-cartao ng-tpl-previa${larga ? ' larga' : ''}`}>
                <div className="ng-tpl-previa-cab">
                  <h2>Prévia</h2>
                  {larga && (
                    <div className="ng-tpl-largura" role="group" aria-label="Largura da prévia">
                      {LARGURAS_DA_PREVIA.map(l => (
                        <button key={l.id} type="button" aria-pressed={larguraDaPrevia === l.id} className={larguraDaPrevia === l.id ? 'on' : ''} onClick={() => setLarguraDaPrevia(l.id)}>{l.rotulo}</button>
                      ))}
                    </div>
                  )}
                  <button type="button" className="ng-btn" style={{ marginLeft: 'auto' }} onClick={() => setLarga(!larga)}
                          title={larga ? 'Volta a prévia para o tamanho da coluna (Esc)' : 'Abre a prévia na janela inteira, para ver o relatório no layout de desktop'}>
                    {larga ? 'Fechar prévia' : 'Ampliar prévia'}
                  </button>
                </div>
                {relatorios.length > 0 && (
                  <div className="ng-filtros" style={{ margin: '0 0 10px', alignItems: 'flex-end' }}>
                    <label className="ng-campo"><span>Ver o template em</span>
                      <select className="ng-select" value={escolha ? String(escolha.dashboardId) : ''} onChange={e => escolherRelatorio(e.target.value ? Number(e.target.value) : null)} aria-label="Relatório da prévia">
                        <option value="">Página de demonstração</option>
                        {relatorios.map(r => <option key={r.dashboardId} value={r.dashboardId}>{r.nome}{r.publicado ? '' : ' (sem versão publicada)'}</option>)}
                      </select>
                    </label>
                    {escolhido && escolhido.ambientes.length > 1 && (
                      <label className="ng-campo"><span>Ambiente</span>
                        <select className="ng-select" value={escolha?.ambienteId} onChange={e => escolherRelatorio(escolhido.dashboardId, Number(e.target.value))} aria-label="Ambiente da prévia">
                          {escolhido.ambientes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                        </select>
                      </label>
                    )}
                  </div>
                )}
                {escolhido && (
                  <p className="ng-explic" style={{ margin: '0 0 10px' }}>
                    Relatório de verdade, com os dados do modelo e sem a regra por usuário. {nomeDoTemplateDoRelatorio && nomeDoTemplateDoRelatorio !== template.nome
                      ? `Hoje ele usa o template “${nomeDoTemplateDoRelatorio}”; aqui você vê como ficaria com este.` : 'Nada é gravado.'}
                  </p>
                )}
                {erroPrevia && <p className="ng-frase" style={{ color: '#c4362b' }}>{erroPrevia}</p>}
                {previa
                  ? (
                    <div data-testid="quadro-da-previa" className={larga && larguraPx ? 'ng-tpl-quadro estreito' : 'ng-tpl-quadro'} style={larga && larguraPx ? { maxWidth: larguraPx } : undefined}>
                      <RelatorioHtmlFrame html={previa.html} contexto={previa.contexto} altura={larga ? 'calc(100vh - 190px)' : 760} consultar={consultarPrevia} />
                    </div>
                  )
                  : <p className="ng-explic">Montando a prévia…</p>}
              </div>
            </div>
          )}
        </section>
      </div>

      <Janela aberto={!!problemasDaImportacao} titulo="Não foi possível importar" sub="Nada foi alterado. Corrija o arquivo (ou peça ao Claude) e importe de novo." botao="Entendi"
              aoConfirmar={() => setProblemasDaImportacao(null)} aoFechar={() => setProblemasDaImportacao(null)}>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {problemasDaImportacao?.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </Janela>

      <Janela aberto={excluir} titulo="Excluir template" botao="Excluir de vez" perigo aoConfirmar={confirmarExcluir} aoFechar={() => setExcluir(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Excluir <b>{template?.nome}</b>?{usos ? ` ${plural(usos, 'relatório usa', 'relatórios usam')} este template e vão passar a usar o padrão.` : ''}</p>
      </Janela>
    </div>
  )
}
